import { editor, MarkerSeverity } from 'monaco-editor-core';
import {
  LuceneAstExpressionNode,
  LuceneAstNode,
  LuceneAstRangeNode,
  LuceneAstValueNode,
  LuceneFieldSchema,
  LuceneModelParsedSchema,
} from './interface';

export class LuceneValidator {
  private static instance: LuceneValidator;

  public static getInstance() {
    if (!this.instance) {
      this.instance = new LuceneValidator();
    }

    return this.instance;
  }

  private constructor() {}

  public async doValidate(ast: LuceneAstNode, options: LuceneModelParsedSchema, markers: editor.IMarkerData[]) {
    try {
      await this.validate(ast, options, markers);
    } catch (error) {
      console.error('could not validate', error);
    }
  }

  private async validate(ast: LuceneAstNode, options: LuceneModelParsedSchema, markers: editor.IMarkerData[]) {
    if (ast === null || ast === undefined) {
      return;
    }
    if (ast['left']) {
      await this.validateExpression(ast as LuceneAstExpressionNode, options, markers);
    } else if (ast['field']) {
      await this.validateValue(ast as LuceneAstValueNode, options, markers);
    }
  }

  private async validateExpression(
    ast: LuceneAstExpressionNode,
    options: LuceneModelParsedSchema,
    markers: editor.IMarkerData[]
  ) {
    await this.validate(ast['left'], options, markers);
    await this.validate(ast['right'], options, markers);
  }

  private async validateValue(
    ast: LuceneAstValueNode,
    options: LuceneModelParsedSchema,
    markers: editor.IMarkerData[]
  ) {
    const field = options.fields.items.find(item => item.name === ast.field);
    if (field) {
      await this.validateField(ast, field, markers);
    } else if (options.fields.forceSelection) {
      markers.push({
        severity: MarkerSeverity.Error,
        startLineNumber: ast.location.start.line,
        startColumn: ast.location.start.column,
        endLineNumber: ast.location.end.line,
        endColumn: ast.field === '<implicit>' ? ast.location.end.column : ast.location.start.column + ast.field.length,
        message: options.fields.invalidMessage,
      });
    } else {
      await this.validateTerm(ast, options, markers);
    }
  }

  private async validateField(
    ast: LuceneAstValueNode | LuceneAstRangeNode,
    field: LuceneFieldSchema,
    markers: editor.IMarkerData[]
  ) {
    if (!field.forceSelection) {
      return;
    }

    if (ast['term']) {
      await this.validateFieldWithValue(ast as LuceneAstValueNode, field, markers);
    }

    if (ast['term_max']) {
      await this.validateFieldWithRange(ast as LuceneAstRangeNode, field, markers);
    }
  }

  private async validateFieldWithValue(
    ast: LuceneAstValueNode,
    field: LuceneFieldSchema,
    markers: editor.IMarkerData[]
  ) {
    let valid: boolean;

    if (Array.isArray(field.values)) {
      valid = field.values.filter(item => item.name === ast.term).length > 0;
    } else {
      valid = await field.values.validate(ast.term);
    }

    if (!valid) {
      markers.push({
        severity: MarkerSeverity.Error,
        startLineNumber: ast.location.start.line,
        startColumn: ast.location.start.column + field.name.length + 1,
        endLineNumber: ast.location.end.line,
        endColumn: ast.location.end.column,
        message: field.invalidMessage,
      });
    }
  }

  private async validateFieldWithRange(
    ast: LuceneAstRangeNode,
    field: LuceneFieldSchema,
    markers: editor.IMarkerData[]
  ) {
    // TODO: needs more research
  }

  private async validateTerm(ast: LuceneAstValueNode, options: LuceneModelParsedSchema, markers: editor.IMarkerData[]) {
    if (!options.terms.forceSelection) {
      return;
    }

    const valid = options.terms.items.filter(item => item.name === ast.term).length > 0;
    if (!valid) {
      markers.push({
        severity: MarkerSeverity.Error,
        startLineNumber: ast.location.start.line,
        startColumn: ast.location.start.column,
        endLineNumber: ast.location.end.line,
        endColumn: ast.location.end.column,
        message: options.terms.invalidMessage,
      });
    }
  }
}
